import React, { useState } from "react";
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Menu,
    MenuItem,
    TextField,
    Tooltip,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import FrontendLoggerFactory from "utils/logging/FrontendLoggerFactory";

const logger = FrontendLoggerFactory.getLogger().withComponent(
    "DevLogDownloadButton",
);

type JsonPrimitive = string | number | boolean | null;
type JsonValue = JsonPrimitive | JsonValue[] | { [key: string]: JsonValue };
export type SessionLogRecord = { [key: string]: JsonValue };

const isLocalhost = (): boolean => {
    if (typeof window === "undefined") {
        return false;
    }

    const hostname = window.location.hostname.toLowerCase();
    return (
        hostname === "localhost" ||
        hostname === "127.0.0.1" ||
        hostname === "0.0.0.0" ||
        hostname.endsWith(".local")
    );
};

const isJsonObject = (
    value: JsonValue,
): value is { [key: string]: JsonValue } => {
    return typeof value === "object" && value !== null && !Array.isArray(value);
};

export const parseSessionLogBuffer = (
    rawLogs: string | null,
): SessionLogRecord[] | null => {
    if (!rawLogs) {
        return null;
    }

    const parsed = JSON.parse(rawLogs) as JsonValue;
    if (!Array.isArray(parsed)) {
        return [];
    }

    return parsed.filter(isJsonObject);
};

export const filterSessionLogsByMessage = (
    logs: readonly SessionLogRecord[],
    filterTerm: string,
): SessionLogRecord[] => {
    const lowerFilterTerm = filterTerm.toLowerCase();
    return logs.filter(log => {
        const message = log.message;
        return (
            typeof message === "string" &&
            message.toLowerCase().includes(lowerFilterTerm)
        );
    });
};

const downloadBlob = (payload: string, filename: string) => {
    const blob = new Blob([payload], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");

    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
};

export const DevLogDownloadButton: React.FC = () => {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [isFilterDialogOpen, setFilterDialogOpen] = useState(false);
    const [filterTermInput, setFilterTermInput] = useState("");
    const open = Boolean(anchorEl);

    if (!isLocalhost()) {
        return null;
    }

    const closeMenu = () => {
        setAnchorEl(null);
    };

    const closeFilterDialog = () => {
        setFilterDialogOpen(false);
        setFilterTermInput("");
    };

    const handleDownload = async () => {
        try {
            await FrontendLoggerFactory.flush();
            const parsed = parseSessionLogBuffer(
                localStorage.getItem(
                FrontendLoggerFactory.getSessionLogStorageKey(),
                ),
            );
            if (!parsed) {
                logger.warn("No logs found in local storage session buffer.");
                return;
            }

            if (parsed.length === 0) {
                logger.warn("Session log buffer is empty.");
                return;
            }

            downloadBlob(
                JSON.stringify(parsed, null, 2),
                `frontend-admin-logs-${new Date().toISOString()}.json`,
            );
        } catch (error) {
            logger.error("Failed to download session logs", error as Error);
        }
    };

    const handleDownloadFiltered = async (filterTerm: string) => {
        try {
            await FrontendLoggerFactory.flush();
            const parsed = parseSessionLogBuffer(
                localStorage.getItem(
                    FrontendLoggerFactory.getSessionLogStorageKey(),
                ),
            );

            if (!parsed) {
                logger.warn("No logs found in local storage session buffer.");
                return;
            }

            const filtered = filterSessionLogsByMessage(parsed, filterTerm);

            if (filtered.length === 0) {
                logger.warn(
                    `No logs found matching "${filterTerm}".`,
                );
                return;
            }

            downloadBlob(
                JSON.stringify(filtered, null, 2),
                `frontend-admin-logs-filtered-${new Date().toISOString()}.json`,
            );
        } catch (error) {
            logger.error(
                "Failed to download filtered session logs",
                error as Error,
            );
        }
    };

    const handleOpenDownloadFilteredDialog = () => {
        closeMenu();
        setFilterDialogOpen(true);
    };

    const handleConfirmDownloadFiltered = async () => {
        const normalizedFilterTerm = filterTermInput.trim();
        if (!normalizedFilterTerm) {
            closeFilterDialog();
            return;
        }

        await handleDownloadFiltered(normalizedFilterTerm);
        closeFilterDialog();
    };

    const handleClear = () => {
        try {
            localStorage.removeItem(FrontendLoggerFactory.getSessionLogStorageKey());
            window.dispatchEvent(new Event("HC_CLEAR_LOGS"));
            logger.info("Session logs cleared.");
        } catch (error) {
            logger.error("Failed to clear session logs", error as Error);
        }
        closeMenu();
    };

    return (
        <Box
            sx={{
                position: "fixed",
                bottom: 70,
                right: 20,
                zIndex: 9999,
                display: "flex",
                alignItems: "center",
                backgroundColor: "rgba(0, 0, 0, 0.6)",
                borderRadius: "24px",
                padding: "4px",
                opacity: 0.3,
                transition: "opacity 0.2s",
                "&:hover": {
                    opacity: 1,
                },
            }}
        >
            <Tooltip title="Download Session Logs">
                <IconButton onClick={handleDownload} size="small" sx={{ color: "white" }}>
                    <DownloadIcon fontSize="small" />
                </IconButton>
            </Tooltip>

            <Tooltip title="Log Options">
                <IconButton
                    onClick={event => setAnchorEl(event.currentTarget)}
                    size="small"
                    sx={{ color: "white" }}
                >
                    <MoreVertIcon fontSize="small" />
                </IconButton>
            </Tooltip>

            <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={closeMenu}
                anchorOrigin={{
                    vertical: "top",
                    horizontal: "left",
                }}
                transformOrigin={{
                    vertical: "bottom",
                    horizontal: "left",
                }}
            >
                <MenuItem onClick={handleOpenDownloadFilteredDialog}>
                    Download with Filter...
                </MenuItem>
                <MenuItem onClick={handleClear}>Clear Buffer</MenuItem>
            </Menu>
            <Dialog
                open={isFilterDialogOpen}
                onClose={closeFilterDialog}
                aria-labelledby="dev-log-filter-dialog-title"
            >
                <DialogTitle id="dev-log-filter-dialog-title">
                    Download filtered logs
                </DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        fullWidth
                        margin="dense"
                        label="Filter text (case insensitive)"
                        value={filterTermInput}
                        onChange={event => setFilterTermInput(event.target.value)}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeFilterDialog}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={() => {
                            void handleConfirmDownloadFiltered();
                        }}
                    >
                        Download
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};
